import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DefaultDialogBoxComponent } from '@shared/components/dialog-boxes/default-dialog-box/default-dialog-box.component';
import { PepDialogBoxComponent } from '@shared/components/dialog-boxes/pep-dialog-box/pep-dialog-box.component';
import { Status } from '@core/models/user';
import { QuestionTemplate } from '../ui/questions/question-template';
import { AdminService } from '@core/services/admin.service';
import { Agree21Service } from './agree21.service';
import { AuthService } from '@core/services/auth.service';
import { DataService } from '@core/services/data.service';
import { LoadingService } from '@core/services/loading.service';
import { QuestionService } from './question.service';
import { DialogService } from '@core/services/dialog.service';

@Injectable({
  providedIn: 'root',
})
export class ButtonService {
  isSaveAndContinueDisabled = false;

  private readonly router = inject(Router);
  private readonly dialog = inject(DialogService);
  private readonly questionService = inject(QuestionService);
  private readonly auth = inject(AuthService);
  private readonly adminService = inject(AdminService);
  private readonly dataService = inject(DataService);
  private readonly agree21Service = inject(Agree21Service);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);

  private answerFormGroup: FormGroup;

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.resetButton());
  }

  handleSaveAndDisableButton(): void {
    this.isSaveAndContinueDisabled = true;

    const component = this.currentComponent();

    if (component?.questionName === 'bank-wahl') {
      this.processBankWahl(component);
      return;
    }

    this.loadingService.show();
    this.saveAndContinueDefault(component);
  }

  isButtonDisabled(): boolean {
    const component = this.currentComponent();

    // bank-wahl wird nicht über den Button gesperrt: die Abtretung wird per Dialog
    // in processBankWahl() bestätigt, ein deaktivierter Button würde diesen blockieren.
    if (component?.questionName === 'bank-wahl') {
      return false;
    }

    return !!this.answerFormGroup && !this.answerFormGroup.valid;
  }

  private currentComponent(): QuestionTemplate | undefined {
    const currentPath = this.questionService.getCurrentPath();
    const questionObj = this.questionService.getQuestionObjByRoute(currentPath);

    if (questionObj?.component) {
      this.answerFormGroup = questionObj.component.answerFormGroup;
    }

    return this.questionService.getComponent(this.questionService.getQuestionIndexByRoute(currentPath));
  }

  private resetButton(): void {
    this.isSaveAndContinueDisabled = false;
    this.loadingService.hide();
  }

  private confirmAbtretungsvertrag(): Observable<boolean> {
    return this.dialog
      .open(DefaultDialogBoxComponent, {
        disableClose: true,
        data: {
          headerText: 'PRODUKTAUSWAHL.BESTÄTIGUNG_PRODUKTAUSWAHL_ABTRETUNGSVERTRAGHINWEISE_HEADER',
          subText: 'PRODUKTAUSWAHL.BESTÄTIGUNG_PRODUKTAUSWAHL_ABTRETUNGSVERTRAGHINWEISE_TEXT',
        },
      })
      .afterClosed();
  }

  private processBankWahl(component: QuestionTemplate): void {
    const abtretung = this.answerFormGroup?.get('abtretungsvertrag');
    const kontokorrentkredit = !!this.answerFormGroup?.get('kontokorrentkredit')?.value;

    if (!kontokorrentkredit) {
      if (abtretung) {
        abtretung.setValue(false, { emitEvent: false });
        abtretung.disable({ emitEvent: false });
        abtretung.markAsPristine();
      }
      this.continueBankWahl(component);
      return;
    }

    this.confirmAbtretungsvertrag()
      .pipe(take(1))
      .subscribe((accepted) => {
        if (!accepted) {
          this.resetButton();
          return;
        }

        if (abtretung) {
          abtretung.setValue(true, { emitEvent: false });
          abtretung.enable({ emitEvent: false });
          abtretung.markAsDirty();
          abtretung.markAsTouched();
        }
        this.continueBankWahl(component);
      });
  }

  private continueBankWahl(component: QuestionTemplate): void {
    this.loadingService.show();
    this.answerFormGroup.updateValueAndValidity({ emitEvent: false });
    this.adminService.updateSubStatus(this.auth.user._id, 'inProcess').subscribe(() => this.saveAnswer(component));
  }

  private saveAndContinueDefault(component: QuestionTemplate | undefined): void {
    if (!component) {
      this.resetButton();
      return;
    }

    if (component.questionName === 'blanko-dokumente') {
      this.processBlankoDokumente(component);
      return;
    }

    if (component.questionName === 'einsicht') {
      this.processEinsicht(component);
      return;
    }

    this.saveAnswer(component);
  }

  private async processBlankoDokumente(component: QuestionTemplate): Promise<void> {
    const offentlichesAmt = this.answerFormGroup.controls?.['offentlichesAmt'].value;

    if (offentlichesAmt !== true) {
      this.saveAnswer(component);
      return;
    }

    const confirmed = await firstValueFrom(this.dialog.open(PepDialogBoxComponent).afterClosed());
    if (confirmed !== true) {
      return;
    }

    const userId = this.auth.user._id;
    this.adminService
      .sendPepEmail(userId)
      .pipe(
        switchMap(() => this.adminService.updateSubStatus(userId, 'pep')),
        switchMap(() => this.auth.changeStatus(userId, Status.inactive))
      )
      .subscribe(() => (window.location.href = environment.oneDashboardURL));
  }

  private processEinsicht(component: QuestionTemplate): void {
    const userId = this.auth.user._id;

    if (!this.auth.user?.giroCardOrdered) {
      this.auth.refreshUser().subscribe((user) => {
        const index = this.auth.findFlaggedNaturalPerson(user.natPersonen);
        const personennummer = user.isJurPerson
          ? user.natPersonen[index].BBKundennummer
          : (this.agree21Service.PERSONEN_NUMMER ?? user.kundenNummer);

        this.orderGirocard(personennummer);
      });
    }

    this.saveAnswer(component);
    this.dataService.archiveAlldocuments().subscribe(() => {});
    this.auth.changeStatus(userId, Status.finish).subscribe(() => {});

    if (this.auth.user.questions[0].answer.kontoWechselService === true) {
      this.adminService.updateSubStatus(userId, 'kws').subscribe(() => {});
    }
  }

  private orderGirocard(personennummer: string | number | undefined): void {
    const iban = this.agree21Service.IBAN;
    this.agree21Service
      .postGirocardOnlineBanking(personennummer, iban, iban, this.auth.getOnlineBankingPublicKey())
      .subscribe((result) => {
        if (!result) {
          throw new Error(`Failed - girocardOnlineBanking for ${this.auth.isJurPerson() ? 'JurPerson' : 'NatPerson'}`);
        }
      });
  }

  private saveAnswer(component: QuestionTemplate): void {
    if (!component.answerFormGroup || !component.answerFormGroup.valid) {
      this.questionService.nextStep();
      return;
    }

    component.saveAnswer().subscribe({
      next: () => this.questionService.nextStep(),
      error: () => setTimeout(() => window.location.reload(), 1000),
    });
  }
}
